import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ScrollReveal from '../shared/ScrollReveal'
import GradientText from '../shared/GradientText'
import { Icons } from '../shared/Icons'
import './CycleSection.css'

const cycleStages = [
  {
    id: 'monitor',
    icon: Icons.pulse,
    title: 'Monitor',
    shortDesc: 'Real-time data',
    description: 'Stream 30+ metrics from inverters, batteries, and sensors with sub-second latency. Six aggregation levels from raw to yearly.',
    stats: ['30+ metrics', 'WebSocket streaming', '6 aggregation levels']
  },
  {
    id: 'detect',
    icon: Icons.search,
    title: 'Detect',
    shortDesc: 'Fault classification',
    description: 'ML-powered fault detection across 8+ categories. Automatic classification with confidence scoring and severity assessment.',
    stats: ['8+ fault types', 'ML classification', '0-100% confidence']
  },
  {
    id: 'predict',
    icon: Icons.trending,
    title: 'Predict',
    shortDesc: 'Failure forecasting',
    description: 'RUL estimation predicts failures 30-180 days in advance. Risk ranking identifies your top 10 highest-priority components.',
    stats: ['30-180 day lead time', 'RUL estimation', 'Top 10 risk ranking']
  },
  {
    id: 'alert',
    icon: Icons.alert,
    title: 'Alert',
    shortDesc: 'Multi-channel',
    description: 'Five alert types with configurable thresholds. Email, SMS, and in-app notifications ensure nothing goes unnoticed.',
    stats: ['5 alert types', 'Threshold config', 'Multi-channel']
  },
  {
    id: 'maintain',
    icon: Icons.wrench,
    title: 'Maintain',
    shortDesc: 'Task management',
    description: 'Automatic task generation with technician assignment. Priority-based scheduling optimizes your workforce efficiency.',
    stats: ['Auto task creation', 'Technician assignment', 'Priority scheduling']
  },
  {
    id: 'optimize',
    icon: Icons.bolt,
    title: 'Optimize',
    shortDesc: 'Smart dispatch',
    description: 'MILP-based battery optimization maximizes grid revenue. Load balancing and price-based charging reduce costs.',
    stats: ['MILP optimization', 'Grid price integration', 'Revenue maximization']
  }
]

export default function CycleSection() {
  const [activeStage, setActiveStage] = useState(0)

  return (
    <section className="cycle-section" id="cycle">
      <div className="container">
        <ScrollReveal className="cycle-header">
          <span className="cycle-badge">// THE OPERATIONAL LOOP</span>
          <h2 className="cycle-title">
            From monitor to optimize.{' '}
            <GradientText gradient="energy">A complete cycle.</GradientText>
          </h2>
          <p className="cycle-subtitle">
            Remaining useful life · Risk score · Health score · Confidence — always on.
          </p>
        </ScrollReveal>

        {/* Cycle diagram */}
        <ScrollReveal delay={0.2}>
          <div className="cycle-diagram">
            {/* Connection lines */}
            <svg className="cycle-lines" viewBox="0 0 1000 100">
              <defs>
                <linearGradient id="line-gradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#FFD700" />
                  <stop offset="50%" stopColor="#00FF88" />
                  <stop offset="100%" stopColor="#00D4FF" />
                </linearGradient>
              </defs>
              {/* Forward arrows */}
              {[0, 1, 2, 3, 4].map((i) => (
                <motion.path
                  key={i}
                  d={`M${130 + i * 156} 50 L${230 + i * 156} 50`}
                  stroke="url(#line-gradient)"
                  strokeWidth="2"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
                />
              ))}
              {/* Return loop */}
              <motion.path
                d="M910 50 Q950 50 950 100 L950 120 Q950 140 900 140 L100 140 Q50 140 50 100 L50 50 Q50 30 90 30"
                stroke="rgba(255, 215, 0, 0.3)"
                strokeWidth="1"
                strokeDasharray="4 4"
                fill="none"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                transition={{ delay: 1, duration: 1 }}
              />
            </svg>

            {/* Stage nodes */}
            <div className="cycle-nodes">
              {cycleStages.map((stage, i) => (
                <motion.button
                  key={stage.id}
                  className={`cycle-node ${activeStage === i ? 'active' : ''}`}
                  onClick={() => setActiveStage(i)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * i }}
                >
                  <span className="cycle-node-icon">
                    <stage.icon size={24} />
                  </span>
                  <span className="cycle-node-title">{stage.title}</span>
                  <span className="cycle-node-desc">{stage.shortDesc}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </ScrollReveal>

        {/* Stage details */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStage}
            className="cycle-details"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="cycle-details-content">
              <div className="cycle-details-header">
                <span className="cycle-details-icon">
                  {(() => {
                    const IconComponent = cycleStages[activeStage].icon
                    return <IconComponent size={28} />
                  })()}
                </span>
                <h3>{cycleStages[activeStage].title}</h3>
              </div>
              <p>{cycleStages[activeStage].description}</p>
              <div className="cycle-details-stats">
                {cycleStages[activeStage].stats.map((stat, i) => (
                  <span key={i} className="cycle-stat-chip">{stat}</span>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Loop indicator */}
        <ScrollReveal delay={0.5} className="cycle-loop-text">
          <span>
            <Icons.refresh size={16} /> Continuous improvement loop
          </span>
        </ScrollReveal>
      </div>
    </section>
  )
}
